import {GitHubAvatar} from '@github-ui/github-avatar'
import {Stack, Text} from '@primer/react'
import {Dialog} from '@primer/react/experimental'

import styles from './AgentAuthorizationDialog.module.css'
import type {AgentAuthorizationAgent} from './types'

export type AgentAuthorizationDialogProps = {
  agent: AgentAuthorizationAgent
  /** Called when the user dismisses the dialog (Cancel, close button, escape, or backdrop). */
  onClose: () => void
  /** Called when the user clicks Authorize. The host is responsible for navigating
   *  to the OAuth start URL — typically `window.location.href = authorizationUrl`. */
  onAuthorize: () => void
  /**
   * When true, the Authorize button shows a pending state (disabled + label
   * change) while the host is fetching the single-use OAuth start URL.
   */
  isAuthorizing?: boolean
  /**
   * Optional error message to display in the dialog body when the most recent
   * Authorize attempt failed. The dialog stays open so the user can retry.
   */
  authorizationError?: string
}

/**
 * Modal prompting the viewer to grant per-user authorization to an agent before
 * it can act on their behalf. Used by picker surfaces + the composer banner.
 */
export function AgentAuthorizationDialog({
  agent,
  onClose,
  onAuthorize,
  isAuthorizing = false,
  authorizationError,
}: AgentAuthorizationDialogProps) {
  const authorizeLabel = isAuthorizing ? 'Authorizing…' : 'Authorize'
  return (
    <Dialog
      onClose={onClose}
      title={`${agent.displayName} requires authorization`}
      width="medium"
      footerButtons={[
        {content: 'Cancel', onClick: onClose, buttonType: 'normal', disabled: isAuthorizing},
        {content: authorizeLabel, onClick: onAuthorize, buttonType: 'primary', disabled: isAuthorizing},
      ]}
    >
      <Stack direction="vertical" gap="condensed">
        {agent.avatarUrl && (
          <Stack direction="horizontal" gap="condensed" align="center">
            <GitHubAvatar src={agent.avatarUrl} alt="" size={20} square />
            <Text weight="semibold">{agent.displayName}</Text>
          </Stack>
        )}
        <p className={styles.bodyText}>
          {agent.displayName} needs your authorization before it can act on your behalf. You&rsquo;ll be redirected to{' '}
          {agent.displayName} to complete authentication, then returned here.
        </p>
        {authorizationError && (
          <p className={styles.errorText} role="alert">
            {authorizationError}
          </p>
        )}
      </Stack>
    </Dialog>
  )
}
